// Implements: F-UIX-019, F-UIX-007, F-GEN-036
/**
 * The revision history as the redesign's timeline card (UX-11): each revision with its source,
 * moment, author and one badge per field it changed. A field or a source the dictionary does not
 * know is shown as the server named it, never dropped.
 */

import { useId } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import type { TranslationKey } from "../../../shared/i18n";
import { Badge } from "../../../shared/ui/atoms/Badge";
import { Card } from "../../../shared/ui/atoms/Card";
import { Skeleton } from "../../../shared/ui/atoms/Skeleton";
import { ErrorState } from "../../../shared/ui/molecules/ErrorState";
import type { ApiFailure } from "../../../types/errors";
import type { RevisionItem } from "../../../types/modelSettings";
import { sourceKey } from "../model/sourceLabel";
import { instantText } from "../model/status";
import styles from "./ModelSettings.module.css";

const FIELD_LABELS = {
  "tiers.fast.model_id": "admin.modelSettings.field.fast.model",
  "tiers.fast.max_output_tokens": "admin.modelSettings.field.fast.cap",
  "tiers.fast.reasoning_effort": "admin.modelSettings.field.fast.effort",
  "tiers.capable.model_id": "admin.modelSettings.field.capable.model",
  "tiers.capable.max_output_tokens": "admin.modelSettings.field.capable.cap",
  "tiers.capable.reasoning_effort": "admin.modelSettings.field.capable.effort",
  message_max_chars: "admin.modelSettings.field.message",
} as const satisfies Record<string, TranslationKey>;

type FieldLabelKey = (typeof FIELD_LABELS)[keyof typeof FIELD_LABELS] | "admin.modelSettings.field.prices";

function labelKey(field: string): FieldLabelKey | null {
  if (field === "prices" || field.startsWith("prices.")) return "admin.modelSettings.field.prices";
  return Object.hasOwn(FIELD_LABELS, field) ? FIELD_LABELS[field as keyof typeof FIELD_LABELS] : null;
}

export interface ModelSettingsHistoryProps {
  readonly items: readonly RevisionItem[] | undefined;
  /** The history could not be read: it says so and offers `onRetry`, instead of loading forever. */
  readonly error?: ApiFailure | null | undefined;
  readonly onRetry?: (() => void) | undefined;
}

export function ModelSettingsHistory({ items, error, onRetry }: ModelSettingsHistoryProps): ReactElement {
  const { t } = useTranslation();
  const headingId = useId();

  function sourceLabel(source: string): string {
    const key = sourceKey(source);
    return key === null ? source : t(key);
  }

  function fieldNames(fields: readonly string[]): string[] {
    return [
      ...new Set(
        fields.map((field) => {
          const key = labelKey(field);
          return key === null ? field : t(key);
        }),
      ),
    ];
  }

  let body: ReactElement;
  if (items === undefined && error !== null && error !== undefined) {
    body = <ErrorState error={error} onRetry={onRetry} headingLevel={3} />;
  } else if (items === undefined) {
    body = (
      <div className={styles.historyLoading} role="status" aria-busy="true">
        <span className={styles.srOnly}>{t("admin.modelSettings.history.loading")}</span>
        <Skeleton width="threeQuarters" />
        <Skeleton width="half" />
        <Skeleton width="threeQuarters" />
      </div>
    );
  } else if (items.length === 0) {
    body = <p className={styles.hint}>{t("admin.modelSettings.history.empty")}</p>;
  } else {
    body = (
      <ol className={styles.timeline}>
        {items.map((item) => {
          const names = fieldNames(item.changed_fields);
          return (
            <li key={item.revision} className={styles.revision}>
              <p>
                <b>{t("admin.modelSettings.history.item", { revision: String(item.revision) })}</b>
                <span className={styles.meta}>
                  {" · "}
                  {sourceLabel(item.source)}
                  {" · "}
                  {instantText(item.created_at)}
                </span>
              </p>
              <p className={styles.meta}>
                {item.author_name === null
                  ? t("admin.modelSettings.history.noAuthor")
                  : t("admin.modelSettings.history.by", { name: item.author_name })}
              </p>
              {names.length === 0 ? (
                <p className={styles.meta}>{t("admin.modelSettings.history.nothing")}</p>
              ) : (
                <p className={styles.fields}>
                  {names.map((name) => (
                    <Badge key={name} tone="neutral">
                      {name}
                    </Badge>
                  ))}
                </p>
              )}
            </li>
          );
        })}
      </ol>
    );
  }

  return (
    <Card as="section" labelledBy={headingId} padded>
      <h2 id={headingId} className={styles.historyTitle}>
        {t("admin.modelSettings.history.title")}
      </h2>
      {body}
    </Card>
  );
}
